import { isNotAuditor } from "./teams";
import { useFabriqStore } from "@/store/fabriq";
import { useRoutinesStore } from "@/store/routines";
import { useTeamsStore } from "@/store/teams";
import { Routine, Team } from "@/types";
import { computed, ComputedRef } from "vue";
import {
  buildPermission,
  PermissionObject,
  PermissionSetupError,
} from "@/composables/permissions/permissions";

type RoutinePermissions = {
  startInstance: ComputedRef<boolean>;
  startInstanceFromRoutineId(routineId: number): boolean;
};

export function useRoutinePermissions(): PermissionObject<
  "routine",
  RoutinePermissions,
  false
>;

export function useRoutinePermissions(
  routineIdForPermission: number
): PermissionObject<"routine", RoutinePermissions, true>;

export function useRoutinePermissions(
  routineIdForPermission?: number
): PermissionObject<"routine", RoutinePermissions, boolean> {
  const fabriqStore = useFabriqStore();

  const currentUserId = computed<number | undefined>(
    () => fabriqStore.user?.id
  );
  const routineStore = useRoutinesStore();
  const teamsStore = useTeamsStore();

  function canStartInstance(routineId: number) {
    const userId = currentUserId.value;
    if (!userId) return false;

    const instanceRoutine: Routine | undefined = (() => {
      return routineStore.collection.find((r: Routine) => r.id === routineId);
    })();
    if (!instanceRoutine) return false;

    return instanceRoutine.teams.some((teamId) => {
      const team: Team = teamsStore.collection.find(
        (t: Team) => t.id === teamId
      );
      if (!team) return false;
      return isNotAuditor(team, userId);
    });
  }

  return buildPermission("routine", Boolean(routineIdForPermission), {
    startInstance: computed(() => {
      if (!routineIdForPermission) {
        throw new PermissionSetupError();
      }
      return canStartInstance(routineIdForPermission);
    }),
    startInstanceFromRoutineId: (routineId: number) => {
      if (!routineId) return false;
      return canStartInstance(routineId);
    },
  });
}
